import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { getBrandingColorPalette } from '../../base/styles';
import { getComputedOuterHeight } from '../functions';

import {
    ContextMenu,
    ContextMenuItem,
    ContextMenuItemGroup
} from './styled';

const Menu = styled(ContextMenu)`
    ${props => props.$palette && css`
        ${ContextMenuItemGroup} {
            background: ${props.$palette.primary};
        }

        ${ContextMenuItem} {
            background: ${props.$palette.accent1} !important;
            color: ${props.$palette.primary} !important;

            svg path {
                fill: ${props.$palette.primary} !important;
            }
        }
    `}
`;

/**
 * The shared menu surface for local participants and server-reported rosters.
 *
 * @param {Object} props - Menu contents, anchor row and interaction callbacks.
 * @param {React.ReactNode} props.children - The menu actions.
 * @param {HTMLElement|null} props.offsetTarget - The anchor row.
 * @param {() => void} [props.onEnter] - Pointer-enter callback.
 * @param {() => void} [props.onLeave] - Pointer-leave callback.
 * @param {() => void} [props.onSelect] - Selection callback.
 * @returns {React$Element<*>}
 */
export const ParticipantContextMenu = ({
    children,
    offsetTarget,
    onEnter = undefined,
    onLeave,
    onSelect
}) => {
    const containerRef = useRef(null);
    const [ isHidden, setIsHidden ] = useState(true);
    const interfaceConfig = useSelector(
        state => state['features/base/config'].interfaceConfig);
    const palette = interfaceConfig?.applyMeetingSettings
        ? getBrandingColorPalette(interfaceConfig) : undefined;

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (container && offsetTarget?.offsetParent instanceof HTMLElement) {
            const {
                offsetTop,
                offsetParent: { offsetHeight, scrollTop }
            } = offsetTarget;
            const outerHeight = getComputedOuterHeight(container);
            const wouldOverflowBottom
                = offsetTop + outerHeight > offsetHeight + scrollTop + 100;

            container.style.top = wouldOverflowBottom
                ? `${Math.max(offsetTop - outerHeight, 0)}px`
                : `${offsetTop}px`;
            setIsHidden(false);
        } else {
            setIsHidden(true);
        }

        // Reposition only when the anchor changes, not on live roster updates.
    }, [ offsetTarget ]);

    return (
        <Menu
            $isHidden = { isHidden }
            $palette = { palette }
            className = 'ignored-child meeting-participant-context-menu'
            onClick = { onSelect }
            onMouseEnter = { onEnter }
            onMouseLeave = { onLeave }
            ref = { containerRef }>
            {children}
        </Menu>
    );
};
